/* One source of truth for what a pick is currently worth, shared by both
   renderers. The grid and the cards must never disagree about whether a pick is
   winning, and the only way to guarantee that is for neither of them to decide. */

/* Structural shapes rather than imports of the query return types, so this stays
   a pure module. Both match the columns fetchGamesForWeek and fetchWeekPicks
   select, so the real rows assign without a cast. */
export type BoardGame = {
  id: string
  home_team: string
  away_team: string
  status: string
  winner: string | null
  home_score: number | null
  away_score: number | null
  kickoff_at: string | null
}

export type BoardPick = {
  user_id: string
  game_id: string
  picked_team: string
  is_lock: boolean
}

export type PickOutcome =
  | 'correct' // final, credited to the picker
  | 'wrong' // final, not credited
  | 'excluded' // final tie in an exclude_game pool: scored by nobody
  | 'leading' // being played, this pick is ahead
  | 'trailing' // being played, this pick is behind
  | 'even' // being played, tied
  | 'pending' // not started, postponed, cancelled, or final with no score

/* tiePolicy is pools.tie_policy, frozen at pool start by trg_pools_freeze_settings.
   It genuinely changes the answer: the same tied game is a correct pick in an
   all_correct pool, a wrong one under no_credit, and scored by nobody under
   exclude_game. weekly_results branches on it for exactly this reason, and a
   board that painted every tie red would contradict the standings on the
   previous screen. */
export function pickOutcome(game: BoardGame, pickedTeam: string, tiePolicy: string): PickOutcome {
  if (game.status === 'final') {
    // winner is a generated column: null unless status is final AND both scores
    // are present. A final with a missing score is unscoreable, not wrong.
    if (game.winner === null) return 'pending'

    if (game.winner === 'tie') {
      if (tiePolicy === 'all_correct') return 'correct'
      if (tiePolicy === 'no_credit') return 'wrong'
      return 'excluded'
    }

    const won =
      (game.winner === 'home' && pickedTeam === game.home_team) ||
      (game.winner === 'away' && pickedTeam === game.away_team)
    return won ? 'correct' : 'wrong'
  }

  if (game.status === 'in_progress') {
    if (game.home_score === null || game.away_score === null) return 'pending'
    if (game.home_score === game.away_score) return 'even'
    const leader = game.home_score > game.away_score ? game.home_team : game.away_team
    return pickedTeam === leader ? 'leading' : 'trailing'
  }

  // scheduled, postponed, cancelled
  return 'pending'
}

/* Gold when the pick is winning or won, red when it is losing or lost, muted
   while there is nothing to say yet. */
export function lockTone(outcome: PickOutcome): 'gold' | 'red' | 'muted' {
  if (outcome === 'correct' || outcome === 'leading') return 'gold'
  if (outcome === 'wrong' || outcome === 'trailing') return 'red'
  return 'muted'
}

export const LOCK_TONE_CLASS: Record<'gold' | 'red' | 'muted', string> = {
  gold: 'text-gold',
  red: 'text-flag',
  muted: 'text-ink-dim',
}

/* A fill is a claim that the result is in, so only the two final states get one.
   'leading' underlines instead: the pick is ahead, which is not the same as
   right, and a green cell at half-time reads as a settled win. */
export const OUTCOME_CLASS: Record<PickOutcome, string> = {
  correct: 'bg-field text-white font-bold',
  wrong: 'text-ink-dim line-through',
  excluded: 'text-ink-dim',
  leading: 'text-ink font-semibold underline decoration-live decoration-2 underline-offset-4',
  trailing: 'text-ink-dim',
  even: 'text-ink',
  pending: 'text-ink',
}

export const OUTCOME_LABEL: Record<PickOutcome, string> = {
  correct: 'correct',
  wrong: 'wrong',
  excluded: 'tie, not scored',
  leading: 'ahead',
  trailing: 'behind',
  even: 'tied',
  pending: 'not started',
}

/* The line under a matchup: score once there is one, kickoff time before that. */
export function gameStatusLabel(game: BoardGame, kickoff: (iso: string) => string) {
  if (game.status === 'cancelled') return 'Cancelled'
  if (game.status === 'postponed') return 'Postponed'

  const score =
    game.away_score !== null && game.home_score !== null
      ? `${game.away_score}-${game.home_score}`
      : null

  if (game.status === 'final') return score ? `Final ${score}` : 'Final'
  if (game.status === 'in_progress') return score ? `Live ${score}` : 'Live'
  return game.kickoff_at ? kickoff(game.kickoff_at) : 'Time TBD'
}

export function indexPicks(picks: BoardPick[]) {
  const byGameAndUser = new Map<string, BoardPick>()
  for (const pick of picks) byGameAndUser.set(`${pick.game_id}:${pick.user_id}`, pick)
  return byGameAndUser
}
